import { addMoney, getMinPrice } from '@shared/lib/money';
import { PriceBreakdownState } from './store/priceBreakdown/reducers';
import { PriceBreakdownType } from './store/priceBreakdown/action';
import { useDispatch } from 'react-redux';
import { Location } from 'history';
import {
	AeroexpressDirection,
	AviaPassengerType,
	BaggageType,
	BookOrderMutationVariables,
	CheckinOrderFragment,
	CheckinPassengerFragment,
	CheckoutOrderFragment,
	FlightFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment as BaggageService,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsServiceVipSubType,
	GetAccountInfoQuery,
	InputTraveller,
	InputTravellerParameter,
	InsuranceProgramFragment,
	NASProductCategory,
	NASProductDirection,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderPromoCodeInfoFragment,
	OrderPromoCodeInfoQueryVariables,
	PassengerFragment,
	PassengerSelectedGdsServicesFragment,
	PriceBreakdownDetailsFragment,
	PromoCodeTravellerParameters,
	SeatType,
	SegmentFragment,
	Subsidy,
	TimelineStep,
	TravellerFieldEnum,
	TravellerGdsServiceService,
	InfoMessagesFragment
} from '@websky/graphql';
import { getIncludedBaggageWeightValue } from '@shared/lib/gdsService';
import {
	FormValues,
	GdsAggregationService,
	GdsAggregationServiceType,
	PassengerValues,
	Segment,
	Step,
	StepType
} from './types';
import { Currency } from '../enums';
import { getSelectedSeatsBySegment } from '../SeatMap/utils';
import { Meal } from '../Meal/types';
import { PassengerState } from './store/passengers/reducers';
import { SubsidiesState } from './store/subsidies/reducers';
import { SelectedMeal } from '../MealPage/types';
import { Baggage, BaggageCount, SelectedBaggageItem, SelectedExtraSeat } from '../BaggageService/types';
import { PassengerBaggage } from '../BaggagePage/types';
import { getRelatedSegments } from '../Baggage/UpgradeBaggageDialog/utils';
import { API_DATE_FORMAT, format, getUserValue, setsAreEqual } from '../utils';
import {
	deleteAllTypesOnSegmentAction_NS,
	ServicePayload,
	setMultipleSelectedServices,
	setMultipleSelectedServices as setMultipleSelectedServicesAction,
	setMultipleSelectedServicesAction_NS
} from './store/selectedServices/actions';
import { Seat, SeatMapCallbacks } from '../SeatMap/types';
import {
	PassengerServices as SelectedPassengerServices,
	SelectedSeatsServiceType,
	SelectedService,
	SelectedServicesState
} from './store/selectedServices/reducers';
import { OrderSubsidyState } from './components/Checkout/Form/SubsidyRequest/subsidyHook';
import { CartServiceGds, TravellerService } from '../Cart/types';
import { PassengerDisabilitiesState } from '../PassengerForm/components/components/Disabilities/types';
import { addDays } from 'date-fns';
import { clearPhoneNumber } from '../PhoneInput/utils';
import { Money } from '../types';
import { UserInfo } from '../Account/types';

export const parsePriceBreakdown = (priceBreakdown: PriceBreakdownDetailsFragment): PriceBreakdownState => {
	const price: PriceBreakdownState = {
		totalPrice: {
			amount: 0,
			currency: Currency.USD
		},
		servicePrice: {
			amount: 0,
			currency: Currency.USD
		},
		upsalePrice: {
			amount: 0,
			currency: Currency.USD
		}
	};

	price.totalPrice = priceBreakdown.price;

	priceBreakdown.parts.forEach(part => {
		if (part.type === PriceBreakdownType.Service) {
			price.servicePrice = addMoney(price.servicePrice, part.price);

			part.parts.forEach(part => {
				if (part.type === PriceBreakdownType.Upsale) {
					price.upsalePrice = addMoney(price.upsalePrice, part.price);
				}
			});
		}
	});

	return price;
};

export const getGdsServiceMinPrice = (
	order: CheckoutOrderFragment,
	gdsService: OrderAdditionalServiceGdsServiceServiceType,
	baggageTypes?: BaggageType[]
) => {
	let prices: Money[] = [];

	if (order.additionalServices?.gdsServices?.services?.length) {
		order.additionalServices.gdsServices.services.forEach(service => {
			if (service.price?.amount > 0 && gdsService !== OrderAdditionalServiceGdsServiceServiceType.Seat) {
				const includeBaggageType =
					!baggageTypes?.length ||
					(baggageTypes?.length && baggageTypes.includes((service as BaggageService).baggageType));

				if (service.type === gdsService && includeBaggageType) {
					prices = [...prices, service.price];
				}
			}
		});
	}

	return getMinPrice(prices, price => price);
};

export const removeServicesStepsByTypes = (steps: Step[], types: StepType[]) => {
	return steps.filter(step => {
		return types.find(type => step.type !== type);
	});
};

const getPassengerDisabilities = (passengerDisabilities: PassengerDisabilitiesState) => {
	const disabilities: string[] = [];

	if (!passengerDisabilities?.enabled) {
		return null;
	}

	Object.entries(passengerDisabilities.values).forEach(([key, isChecked]) => {
		if (isChecked) {
			disabilities.push(key);
		}
	}, null);

	return disabilities.length ? disabilities : null;
};

export const prepareBookingRequest = (
	orderId: string,
	formValues: FormValues,
	orderSubsidyState?: OrderSubsidyState,
	selectedSubsidies?: SubsidiesState['selectedSubsidies']
): BookOrderMutationVariables => {
	const values = {
		params: {
			id: orderId,
			customer: {
				values: [
					{
						name: 'email',
						value: formValues['customer']['email']
					},
					{
						name: 'phone',
						value: clearPhoneNumber(formValues['customer']['phone'])
					}
				],
				subscribed: formValues?.customer?.subscribed ?? false
			},
			travellers: formValues['passengers'].map((passenger: object, index: number) => {
				const values: InputTravellerParameter[] = [];
				const passengerId = index.toString();

				for (const fieldName in passenger) {
					if (passenger.hasOwnProperty(fieldName)) {
						if (!['type', 'disabilities', 'subsidy', 'files'].includes(fieldName)) {
							values.push({
								name: fieldName,
								value:
									fieldName === 'phone'
										? clearPhoneNumber(passenger[fieldName])
										: passenger[fieldName]
							});
						}
					}
				}

				// Определяем источник субсидий
				let passengerSubsidies: Subsidy[] | null = null;

				if (selectedSubsidies) {
					// Новый формат: берём из selectedSubsidies по passengerId
					passengerSubsidies = selectedSubsidies[passengerId] ?? null;
				} else if (orderSubsidyState?.isSubsidiesAvailable) {
					// Старый формат: ищем в orderSubsidyState.travellers
					passengerSubsidies =
						orderSubsidyState.travellers.find(traveller => traveller.traveller.id === passengerId)
							?.subsidies ?? null;
				}

				return {
					id: passengerId,
					values: values,
					subsidies: passengerSubsidies?.length > 0 ? { selectedSubsidies: passengerSubsidies } : null,
					disabilities: getPassengerDisabilities(passenger['disabilities'])
				} as InputTraveller;
			})
		}
	};

	if (formValues['customer']['accompanyingPersonName'] && formValues['customer']['accompanyingPersonTicketNumber']) {
		values.params.customer.values.push(
			{
				name: 'accompanyingPersonName',
				value: formValues['customer']['accompanyingPersonName']
			},
			{
				name: 'accompanyingPersonTicketNumber',
				value: formValues['customer']['accompanyingPersonTicketNumber']
			}
		);
	}

	return values;
};

export const checkInternationalFlight = (flight: FlightFragment): boolean => {
	return flight.segments.some(
		segment => segment.segment.arrival.airport.country.id !== segment.segment.departure.airport.country.id
	);
};

export const getServicesForBaggageIncrease = (
	selectedService: {
		baggage: Baggage;
		segmentId: string;
		passengerId: string;
		forAllSegments?: boolean;
	},
	baggageServices: BaggageService[],
	passengersState: PassengerState[]
): PassengerBaggage[] => {
	let createNewServices = true;
	const { baggage, segmentId, passengerId, forAllSegments } = selectedService;
	const services: Partial<PassengerSelectedGdsServicesFragment>[] = [];
	const baggageMap = new Map(baggageServices.map(baggage => [baggage.id, true]));
	const relatedSegments = getRelatedSegments(baggage.segmentIds, forAllSegments ? null : segmentId);

	passengersState[parseInt(passengerId)].services.forEach(selectedService => {
		if (baggageMap.has(selectedService.serviceId)) {
			if (
				selectedService.serviceId === baggage.id &&
				(forAllSegments || setsAreEqual(relatedSegments, new Set(selectedService.segmentIds)))
			) {
				// Do not create new services, cuz we already have one.
				createNewServices = false;

				services.push({
					...selectedService,
					count: selectedService.count + 1
				});
			} else {
				services.push(selectedService);
			}
		}
	});

	if (createNewServices) {
		services.push({
			serviceId: baggage.id,
			count: 1,
			segmentIds: [...relatedSegments]
		});
	}

	return [
		{
			passengerId,
			services
		}
	];
};

export const getServicesForBaggageDecrease = (
	selectedService: {
		baggage: Baggage;
		segmentId: string;
		passengerId: string;
		forAllSegments?: boolean;
	},
	baggageServices: BaggageService[],
	passengersState: PassengerState[]
): PassengerBaggage[] => {
	const { baggage, segmentId, passengerId, forAllSegments } = selectedService;
	const services: TravellerGdsServiceService[] = [];
	const relatedSegments = getRelatedSegments(baggage.segmentIds, forAllSegments ? null : segmentId);

	passengersState[parseInt(passengerId)].services.forEach(selectedService => {
		if (
			selectedService.serviceId === baggage.id &&
			(forAllSegments || setsAreEqual(relatedSegments, new Set(selectedService.segmentIds)))
		) {
			services.push({
				...selectedService,
				count: selectedService.count > 0 ? selectedService.count - 1 : 0
			});
		} else if (
			selectedService.serviceId !== baggage.id ||
			(selectedService.segmentIds[0] !== segmentId && !forAllSegments)
		) {
			services.push(selectedService);
		}
	});

	return [
		{
			passengerId,
			services
		}
	];
};

export const getServicesForBaggageReplace = (
	oldBaggage: Baggage,
	newBaggage: Baggage,
	segmentId: string,
	passengerId: string,
	forAllSegments: boolean,
	baggageServices: BaggageService[],
	passengersState: PassengerState[]
): PassengerBaggage[] => {
	const services: TravellerGdsServiceService[] = [];
	const relatedSegments = getRelatedSegments(newBaggage.segmentIds, forAllSegments ? null : segmentId);

	// Remove old baggage.
	passengersState[parseInt(passengerId)].services.forEach(service => {
		if (
			service.serviceId === oldBaggage.id &&
			(forAllSegments || setsAreEqual(relatedSegments, new Set(service.segmentIds)))
		) {
			services.push({
				...service,
				count: 0
			});
		} else {
			services.push(service);
		}
	});

	// Add new baggage for given segments.
	services.push({
		serviceId: newBaggage.id,
		count: 1,
		segmentIds: [...relatedSegments]
	});

	return [
		{
			passengerId,
			services
		}
	];
};

export const getServicesForBaggageModeChange = (
	passengerId: string,
	segments: Segment[],
	baggageServices: BaggageService[],
	passengersState: PassengerState[]
): PassengerBaggage[] => {
	const services: BaggageCount = {};

	passengersState[parseInt(passengerId)].services.forEach(service => {
		if (!services[service.serviceId] || services[service.serviceId] < service.count) {
			services[service.serviceId] = service.count;
		}
	});

	const newServices: TravellerGdsServiceService[] = [];

	for (const service in services) {
		if (services.hasOwnProperty(service)) {
			segments.forEach(({ id }) => {
				newServices.push({
					serviceId: service,
					count: services[service],
					segmentIds: [id]
				});
			});
		}
	}

	return [{ passengerId: passengerId, services: newServices }];
};

export const getMealReduxCallbacks = (dispatch: ReturnType<typeof useDispatch>) => {
	return {
		onMealAddForPassengers: (meal: Meal, segmentId: string, passengerIds: string[], count: number) => {
			const services: ServicePayload[] = passengerIds.map(passengerId => ({
				passengerId,
				segmentId,
				serviceId: meal.id,
				count,
				allowedSegments: meal.allowedSegments,
				service: {
					id: meal.id,
					type: OrderAdditionalServiceGdsServiceServiceType.Meal,
					name: meal.name,
					price: meal.price
				}
			}));
			dispatch(setMultipleSelectedServicesAction_NS(services));
		},
		onMealClear: (meal: Meal, segmentId: string) => {
			dispatch(
				deleteAllTypesOnSegmentAction_NS({
					segmentId,
					serviceId: meal.id
				})
			);
		},
		onMealConfirm: (selected: SelectedMeal[], meal: Meal) => {
			dispatch(
				setMultipleSelectedServices(
					selected.map(selected => ({
						passengerId: selected.passengerId,
						segmentId: selected.segmentId,
						serviceId: meal.id,
						allowedSegments: meal.allowedSegments,
						count: selected.count,
						service: {
							id: meal.id,
							type: OrderAdditionalServiceGdsServiceServiceType.Meal,
							name: meal.name,
							price: meal.price
						}
					}))
				)
			);
		}
	};
};

export const isSelectedSeatGuard = (seat: Seat | SelectedSeatsServiceType): seat is SelectedSeatsServiceType =>
	(seat as SelectedSeatsServiceType).isConfirmed !== undefined;

export const convertSeatsToServicePayload = (
	seats: Seat[] | SelectedSeatsServiceType[],
	passengerId: string,
	segmentId: string,
	isExtraSeats?: boolean,
	count?: number
): ServicePayload[] => {
	return seats.map((seat, index) => ({
		segmentId,
		passengerId,
		serviceId: seat.number,
		service: {
			letter: seat.letter,
			row: seat.row,
			id: !isSelectedSeatGuard(seat) ? seat.service?.id : null,
			deckId: null,
			type: OrderAdditionalServiceGdsServiceServiceType.Seat,
			rfisc: seat.rfisc,
			price: seat.price,
			number: seat.number,
			segmentId,
			seatType: index === 0 && isExtraSeats ? SeatType.extraSeat : null
		},
		...(count !== undefined ? { count } : {})
	}));
};

export const convertSeatToServicePayload = (
	seat: Seat | SelectedSeatsServiceType,
	segmentId: string,
	passengerId: string,
	seatType?: SeatType
): ServicePayload => {
	return {
		segmentId,
		passengerId,
		serviceId: seat.number,
		service: {
			segmentId,
			deckId: null,
			id: !isSelectedSeatGuard(seat) ? seat.service?.id : null,
			rfisc: seat.rfisc,
			row: seat.row,
			letter: seat.letter,
			number: seat.number,
			price: seat.price,
			type: OrderAdditionalServiceGdsServiceServiceType.Seat,
			seatType
		}
	};
};

export const getSeatReduxCallbacks = (
	passengers: PassengerState[],
	setMultipleServicesAction: typeof setMultipleSelectedServicesAction
): SeatMapCallbacks => {
	return {
		setSeatCallback: (seats: Seat[] = [], passengerId: string, segmentId: string) => {
			const isExtraSeats = seats.length > 1;

			const key = `${segmentId}/${passengerId}`;
			const sortSeats = [...seats].sort((a, b) => a.letter.localeCompare(b.letter));
			const selectedSeatNumbers = new Set(sortSeats.map(seat => seat.number));

			const seatsToSaveMap = new Map<string, ServicePayload[]>();

			passengers.forEach(passenger => {
				passenger.seats?.forEach(selectedSeat => {
					const selectedSeatSegmentId = selectedSeat.segmentId;
					const selectedSeatPassengerId = passenger.id;

					const seatKey = `${selectedSeatSegmentId}/${selectedSeatPassengerId}`;

					const servicePayload = convertSeatToServicePayload(
						selectedSeat,
						selectedSeatSegmentId,
						selectedSeatPassengerId
					);

					const shouldClearOverlappingSeat =
						isExtraSeats &&
						selectedSeatSegmentId === segmentId &&
						selectedSeatPassengerId !== passengerId &&
						selectedSeatNumbers.has(selectedSeat.number);

					seatsToSaveMap.set(seatKey, [
						...(seatsToSaveMap.get(seatKey) ?? []),
						shouldClearOverlappingSeat ? { ...servicePayload, count: 0 } : servicePayload
					]);
				});
			});

			seatsToSaveMap.set(key, [
				...(seatsToSaveMap.get(key) ?? []).map(seat => ({ ...seat, count: 0 })),
				...sortSeats.map((seat, index) =>
					convertSeatToServicePayload(
						seat,
						segmentId,
						passengerId,
						isExtraSeats
							? index === sortSeats.length - 1
								? SeatType.extraSeat
								: SeatType.common
							: undefined
					)
				)
			]);

			const seatsToSave = [...seatsToSaveMap.values()].reduce((seats, newSeats) => [...seats, ...newSeats], []);

			if (seatsToSave.length) {
				setMultipleServicesAction(seatsToSave);
			}
		},
		setClearSeatCallback: (seats: Seat[], passengerId: string, segmentId: string) => {
			setMultipleServicesAction(convertSeatsToServicePayload(seats, passengerId, segmentId, false, 0));
		},
		onSegmentClear: (segmentId?: number) => {
			let services: ServicePayload[] = [];

			passengers.forEach(passenger => {
				const selectedSeats = passenger.seats?.filter(seat => parseInt(seat.segmentId) === segmentId) || [];

				services = [
					...services,
					...convertSeatsToServicePayload(selectedSeats, passenger.id, segmentId.toString(), false, 0)
				];
			});

			if (services.length) {
				setMultipleServicesAction(services);
			}
		},
		onPassengerClear: (passengerId: string) => {
			let services: ServicePayload[] = [];

			const passengerSeats = passengers.find(passenger => passenger.id === passengerId)?.seats || [];
			passengerSeats.forEach(seat => {
				services = [
					...services,
					...convertSeatsToServicePayload([seat], passengerId, seat.segmentId, false, 0)
				];
			});

			if (services.length) {
				setMultipleServicesAction(services);
			}
		}
	};
};

export const getUserInfo = (userInfo?: GetAccountInfoQuery['CurrentUser']): UserInfo => {
	if (!userInfo || !userInfo?.userProfile) {
		return {
			lastName: '',
			firstName: '',
			email: ''
		};
	}

	return {
		lastName: getUserValue(userInfo.userProfile, TravellerFieldEnum.LastName),
		firstName: getUserValue(userInfo.userProfile, TravellerFieldEnum.FirstName),
		email: getUserValue(userInfo.userProfile, TravellerFieldEnum.Email)
	};
};

type OrderTraveller = PassengerFragment | CheckinPassengerFragment;

export const checkoutSelectedServicesStateFactory = <T extends CheckoutOrderFragment | CheckinOrderFragment>(
	order: T
): SelectedServicesState => {
	const gdsServiceMap = new Map(order.additionalServices.gdsServices?.services?.map(s => [s.id, s]) ?? []);

	const addService = (passengerServices: SelectedPassengerServices, key: string, service: SelectedService): void => {
		const existing = passengerServices[key];

		if (existing) {
			existing.push(service);
		} else {
			passengerServices[key] = [service];
		}
	};

	return (order.travellers as OrderTraveller[]).reduce<SelectedServicesState>((selectedServices, traveller) => {
		const passengerServices: SelectedPassengerServices = {};

		// gds services
		traveller.services.gdsServices?.services?.forEach(service => {
			const gdsService = gdsServiceMap.get(service.serviceId);

			addService(passengerServices, service.serviceId, {
				segments: service.segmentIds,
				number: service.count,
				servicePayload: {
					id: service.serviceId,
					type: gdsService?.type,
					confirmedCount: service.confirmedCount
				}
			});
		});

		// seats
		traveller.services.seats?.forEach(service => {
			const seatKey = `${service.row}${service.letter}`;
			addService(passengerServices, seatKey, {
				segments: [service.segment.id],
				number: 1,
				servicePayload: {
					id: service.seat.seatService?.id,
					type: OrderAdditionalServiceGdsServiceServiceType.Seat,
					letter: service.seat.letter,
					rfisc: service.seat.rfisc,
					price: service.seat.price,
					row: service.row.toString(),
					segmentId: service.segment.id,
					number: `${service.row}${service.letter}`,
					isConfirmed: service.isConfirmed,
					isChangeable: service.isChangeable,
					seatServices: service.seat.seatServices,
					seatService: service.seat.seatService,
					seatType: service.type,
					product: service.product,
					restrictions: service.seat.restrictions
				}
			});
		});

		selectedServices[traveller.id] = passengerServices;

		return selectedServices;
	}, {});
};

export const getIncludedBaggageFromOrderTravellers = (travellers: PassengerFragment[] | CheckinPassengerFragment[]) => {
	const includedBaggages: SelectedBaggageItem[] = [];

	(travellers as PassengerFragment[]).forEach(traveller =>
		traveller.services?.brandIncludedServices?.services
			.filter(services => services.service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage)
			.forEach(service => {
				const baggageService = service.service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment;

				const baggage: Baggage = {
					id: service.serviceId,
					segmentIds: baggageService.allowedSegments,
					includedSegments: service.segmentIds,
					type: baggageService.baggageType,
					name: baggageService.name,
					description: baggageService.description,
					value: getIncludedBaggageWeightValue(baggageService.baggageWeight),
					size: baggageService.size,
					included: service.confirmedCount,
					price: baggageService.price,
					rfics: baggageService.rfisc,
					canBeAdded: false,
					allowedPassengers: [traveller.id]
				};

				includedBaggages.push({
					...service,
					baggage
				});
			})
	);

	return includedBaggages;
};

export const getDefaultTimelineStep = (
	serviceType: GdsAggregationServiceType,
	direction?: AeroexpressDirection | NASProductDirection
): TimelineStep => {
	if (serviceType === 'transfer') {
		if (direction === AeroexpressDirection.ToAirport) {
			return TimelineStep.ToAirport;
		} else {
			return TimelineStep.Landing;
		}
	}

	if (
		serviceType === NASProductCategory.vip ||
		serviceType === NASProductCategory.fast_track ||
		serviceType === NASProductCategory.lounge ||
		serviceType === NASProductCategory.meet_and_greet
	) {
		if (direction === NASProductDirection.Departure) {
			return TimelineStep.Airport;
		} else if (direction === NASProductDirection.Arrival) {
			return TimelineStep.Landing;
		}
	}

	switch (serviceType) {
		case OrderAdditionalServiceGdsServiceServiceType.Seat:
		case OrderAdditionalServiceGdsServiceServiceType.Meal:
		case 'upgrade':
			return TimelineStep.Boarding;

		case OrderAdditionalServiceGdsServiceServiceType.Baggage:
		case OrderAdditionalServiceGdsServiceServiceType.BusinessLounge:
		case OrderAdditionalServiceGdsServiceServiceType.Vip:
		case NASProductCategory.lounge:
		case NASProductCategory.fast_track:
		case NASProductCategory.vip:
		case 'baggageDelivery':
		case 'animal':
		case 'loyalty':
			return TimelineStep.Airport;

		case 'insurance':
		case 'insuranceGroups':
			return TimelineStep.Other;

		default:
			return null;
	}
};

export const getOrderPromoCodeInfoParams = (
	order: CheckoutOrderFragment,
	formValues: object
): OrderPromoCodeInfoQueryVariables => {
	const travellersParam: PromoCodeTravellerParameters[] = [];
	let hasPromoCodes = false;

	order.travellers.forEach(traveller => {
		const travellerParamValues: InputTravellerParameter[] = [];

		let promoCode: string = null;

		traveller.values.forEach(travellerValue => {
			travellerParamValues.push({
				name: travellerValue.name,
				value: formValues['passengers'][traveller.id][travellerValue.name] ?? null
			});

			if (travellerValue.type === TravellerFieldEnum.PromoCode) {
				promoCode = formValues['passengers'][traveller.id][travellerValue.name];

				if (promoCode?.length > 0) {
					hasPromoCodes = true;
				}
			}
		});

		if (promoCode) {
			travellersParam.push({
				id: traveller.id,
				values: travellerParamValues,
				promoCode: promoCode
			});
		}
	});

	return hasPromoCodes
		? {
				id: order.id,
				travellers: travellersParam
		  }
		: null;
};

export const getTotalDiscount = (promoCodesInfo: OrderPromoCodeInfoFragment) => {
	return promoCodesInfo?.travellers?.reduce((total, tr) => total + (+tr.promoCodeInfo?.discount.amount ?? 0), 0) ?? 0;
};

export const getWithValidPromoCodesFormValues = (
	formValues: object,
	promoCodesInfo: OrderPromoCodeInfoFragment,
	valuesNames: Map<TravellerFieldEnum, string>
) => {
	if (promoCodesInfo.success) {
		return formValues;
	}

	const passengers = formValues['passengers'];
	const promoCodeValueName = valuesNames.get(TravellerFieldEnum.PromoCode);

	return {
		...formValues,
		passengers: passengers.map((passenger: any, idx: number) => ({
			...passenger,
			[promoCodeValueName]: promoCodesInfo.travellers[idx]?.promoCodeInfo ? passenger[promoCodeValueName] : null
		}))
	};
};

export const getIsAllSeatsSelected = (passengers: PassengerState[], availableSegments: string[]) => {
	const availableSegmentsCount = availableSegments.length;

	if (availableSegmentsCount === 0) {
		return true;
	}

	// Проходим по каждому пассажиру (кроме младенцев)
	for (const passenger of passengers) {
		if (passenger.type === AviaPassengerType.INF) {
			continue;
		}
		// Собираем сегменты, на которых у этого пассажира выбраны места
		const passengerSegments = new Set<string>();

		if (passenger.seats?.length) {
			passenger.seats.forEach(seat => passengerSegments.add(seat.segmentId));
		}
		// Проверяем, что у пассажира есть места на всех доступных сегментах
		for (const segmentId of availableSegments) {
			if (!passengerSegments.has(segmentId)) {
				return false; // Пассажир не выбрал место на этом сегменте
			}
		}
	}

	return true;
};

export const getIsAllExtraSeatsSelected = (
	services: SelectedExtraSeat[],
	passengers: PassengerState[],
	availableSegmentCount: number
) => {
	if (!services.length) {
		return true;
	}

	const passengerIdsWithExtraSeat = new Set(services.map(service => service.passengerId));

	for (const passenger of passengers) {
		if (passengerIdsWithExtraSeat.has(passenger.id)) {
			const selectedSeatsBySegment = getSelectedSeatsBySegment(passenger.seats);

			if (selectedSeatsBySegment.size !== availableSegmentCount) {
				return false;
			}
		}
	}

	return true;
};

export const mergeDuplicatedTravellerServices: (
	services: TravellerService[] | SelectedBaggageItem[],
	keySelector: (service: TravellerService | SelectedBaggageItem) => string
) => TravellerService[] | SelectedBaggageItem[] = (
	services: TravellerService[] | SelectedBaggageItem[],
	keySelector
) => {
	const servicesByKeyMap = new Map<string, TravellerService | SelectedBaggageItem>();

	services?.forEach(service => {
		const key = keySelector(service);

		if (servicesByKeyMap.has(key)) {
			const serviceToUpdate = servicesByKeyMap.get(key);

			servicesByKeyMap.set(key, {
				...serviceToUpdate,
				count: ((serviceToUpdate.count as number) ?? 0) + ((service.count as number) ?? 0),
				confirmedCount:
					((serviceToUpdate.confirmedCount as number) ?? 0) + ((service.confirmedCount as number) ?? 0)
			});
		} else {
			servicesByKeyMap.set(key, service);
		}
	});

	return [...servicesByKeyMap.values()];
};

export const isPetBaggage = (baggage: BaggageService) => {
	return [BaggageType.PetInCabin, BaggageType.PetInHandCarrier, BaggageType.PetInHold].includes(baggage.baggageType);
};

export const calculateCheckinAvailability = (order: CheckoutOrderFragment) => {
	const today = format(new Date(), API_DATE_FORMAT);
	const tomorrow = format(addDays(new Date(), 1), API_DATE_FORMAT);

	if (
		order.flight.segmentGroups.some(segmentGroup =>
			segmentGroup.segments.some(segment => segment.departure.date === today)
		)
	) {
		return true;
	} else {
		let segmentWithTomorrowDeparture: SegmentFragment = null;

		for (const segmentGroup of order.flight.segmentGroups) {
			segmentWithTomorrowDeparture = segmentGroup.segments.find(segment => segment.departure.date === tomorrow);

			if (segmentWithTomorrowDeparture) {
				break;
			}
		}

		return (
			segmentWithTomorrowDeparture &&
			new Date().getHours() > Number(segmentWithTomorrowDeparture.departure.time.substring(0, 2))
		);
	}
};

export const getIsBrandIncludedService = (
	order: CheckoutOrderFragment,
	serviceType: OrderAdditionalServiceGdsServiceServiceType
) => {
	return order.travellers.some(traveller =>
		traveller.services.brandIncludedServices.services?.some(
			service => service.service?.type === serviceType && service.count > 0
		)
	);
};

export const getServicePrice = (service: GdsAggregationService): Money => {
	if (service?._serviceType === 'insurance') {
		return (service as InsuranceProgramFragment)?.totalPrice;
	}

	return service?.price;
};

/**
 * Return services to delete
 * @param {CartServiceGds} payload - Summary gds service
 * @param {PassengerFragment[]} travellers - Travellers list
 * @returns {CardExtraService[]}
 */
export function getCartServicesToDelete(payload: CartServiceGds, travellers: PassengerFragment[]): ServicePayload[] {
	const removeServices: ServicePayload[] = [];

	const commonRemoveService: Omit<ServicePayload, 'segmentId'> = {
		count: 0,
		serviceId: payload.serviceId,
		passengerId: payload.passengerId,
		allowedSegments: payload.allowedSegments
	};

	if (payload.allSegments) {
		const travellerServicesById = travellers.find(traveller => traveller.id === payload.passengerId)?.services
			?.gdsServices?.services;

		const service = travellerServicesById.find(service => service.serviceId === payload.serviceId);

		if (service) {
			service.segmentIds.forEach(segmentId => {
				removeServices.push({ ...commonRemoveService, segmentId, segmentIds: [segmentId] });
			});
		}
	} else {
		removeServices.push({ ...commonRemoveService, segmentId: payload.segmentId, segmentIds: [payload.segmentId] });
	}

	return removeServices;
}

export function getQueryServicesParams(location: Location<unknown>) {
	const queryParams = new URLSearchParams(location.search);

	const openServiceType = queryParams.get('openService') as OrderAdditionalServiceGdsServiceServiceType;
	const serviceSubType = queryParams.get('subtype') as GdsServiceVipSubType | BaggageType;
	const insuranceCode = queryParams.get('insuranceCode');
	const serviceRfisc = queryParams.get('rfisc');
	const updateOrder = queryParams.get('UpdateOrder') === '1';
	const updateServices = queryParams.get('UpdateServices') === '1';

	return {
		queryParams,
		serviceRfisc,
		openServiceType,
		serviceSubType,
		insuranceCode,
		updateOrder,
		updateServices
	};
}

export function getUrlWithServicesParams(url: string, location: Location): string {
	const { openServiceType, serviceSubType, insuranceCode, updateOrder, updateServices } = getQueryServicesParams(
		location
	);

	let urlWithParams = url;

	if (openServiceType) {
		urlWithParams += `?openService=${openServiceType}`;

		if (serviceSubType) {
			urlWithParams += `&subtype=${serviceSubType}`;
		} else if (insuranceCode) {
			urlWithParams += `&insuranceCode=${insuranceCode}`;
		}
	}

	if (updateOrder) {
		urlWithParams += (urlWithParams.includes('?') ? '&' : '?') + 'UpdateOrder=1';
	}

	if (updateServices) {
		urlWithParams += (urlWithParams.includes('?') ? '&' : '?') + 'UpdateServices=1';
	}

	return urlWithParams;
}

/**
 * Проверяет, соответствуют ли пассажиры условиям указанного кода из сообщения order.messages
 * @param code строка вида "Show_for:CLD:!RU"
 * @param passengers массив пассажиров
 * @returns true или false
 */
export const checkPassengerByCode = (code: string, passengers: PassengerValues[]) => {
	if (!passengers?.length) {
		return false;
	}

	const tokens = code.split(':');

	/** Если код не соответствует виду "Show_for:***:**", то сообщение не проверяем */
	if (tokens.length < 3 || !code.startsWith('Show_for:')) {
		return true;
	}

	const [_, requiredType, rule] = tokens;

	const isExcluded = rule.startsWith('!');
	const requiredNationality = isExcluded ? rule.slice(1) : rule;

	return passengers.some(passenger => {
		if (passenger.type !== requiredType) {
			return false;
		}

		/**
		 * "Show_for:CLD:!RU"
		 * isExcluded === true(есть отрицание !) - проверяем, что в заказе есть CLD и выбрано не RU гражданство
		 * "Show_for:CLD:RU"
		 * isExcluded === false(нет отрицания) - проверяем, что в заказе есть CLD и выбрано RU гражданство
		 */
		return isExcluded
			? passenger.nationality !== requiredNationality
			: passenger.nationality === requiredNationality;
	});
};

/**
 * Фильтрует информационные сообщениея:
 * - пропускает если код соответствует виду Show_for:***:** и подходит одному из passengers
 * - пропускает если код не соответствует виду Show_for:***:**
 * @param messages массив сообщений
 * @param passengers массив пассажиров, которых нужно проверить
 * @returns отфильтрованный массив сообщения
 */
export const filterInfoMessagesByCode = (
	messages: InfoMessagesFragment[],
	passengers: PassengerValues[]
): InfoMessagesFragment[] => {
	return messages.filter(message => checkPassengerByCode(message.code, passengers));
};
